import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiService } from '@app/core/services/api.service';
import { ApprovalRequestType } from '@common/enums/ApprovalRequestType';
import { AttachmentType } from '@common/enums/capitalCall/AttachmentType';
import { IAttachment } from '@common/models/IAttachment';
import { IPortfolioCompany } from '@common/models/IPortfolioCompany';
import { IRepositoryItem } from '@common/models/IRepositoryItem';
import { IStorageStat } from '@common/models/IStorageStat';
import { QEPFileUploadEvent } from 'qep-file-browser/lib/qep-file-browser.interface';
import { Observable } from 'rxjs';
import { Buffer } from 'buffer';
import { InvestmentActivityFileType } from '@common/enums/InvestmentActivity';

@Injectable({
  providedIn: 'root'
})
export class QepFileBrowserService {

  constructor(private apiService: ApiService) { }

  getFiles(): Observable<IRepositoryItem[]> {
    return this.apiService.get<IRepositoryItem[]>('CentralRepository/listAll');
  }

  getPcFiles(mnemonic: string): Observable<IRepositoryItem[]> {
    const res = this.apiService.get<IRepositoryItem[]>(`CentralRepository/allAttachments?code=${mnemonic}`);
    return res;
  }

  deleteIcMemoFile(
  mnemonic: string,
  quarter: string,
  name: string,
  type: string,
  filename: string
): Observable<any> {
  const url = `PortfolioCompany/${mnemonic}/ICMemo/${quarter}/${name}/attachment/${type}/${filename}`;
  return this.apiService.delete(url);
}

deleteTechnicalMaterialsFile(
  mnemonic: string,
  quarter: string,
  name: string,
  type: string,
  filename: string
): Observable<any> {
  const url = `PortfolioCompany/${mnemonic}/technical_materials/${quarter}/${name}/attachment/${type}/${filename}`;
  return this.apiService.delete(url);
}

deleteLegalFile(
  mnemonic: string,
  quarter: string,
  name: string,
  type: string,
  filename: string
): Observable<any> {
  const url = `PortfolioCompany/${mnemonic}/legal/${quarter}/${name}/attachment/${type}/${filename}`;
  return this.apiService.delete(url);
}

  getAllPorfolioCompanies(): Observable<IPortfolioCompany[]> {
    return this.apiService.get<IPortfolioCompany[]>('PortfolioCompany');
  }

  downloadFile(fileName: string, path: string): Observable<Buffer> {
    return this.apiService.post('CentralRepository/download', { fileName, path }, {
      responseType: 'blob' as 'json'
    });
  }

  downloadFiles(path: string, mnemonic: string): Observable<Buffer> {
    return this.apiService.post('CentralRepository/downloadAll', { path }, {
      responseType: 'blob' as 'json',
      params: { mnemonic }
    });
  }

  deleteFile(
    code: string, quarter: string, date: string,
    investmentActivity: string, documentType: string,
    index: number, filename: string
  ): Observable<any> {
    let url: string;
    if (investmentActivity === ApprovalRequestType.Valuation) {
      url = `PortfolioCompany/${code}/Valuation/${quarter}/Attachment/${documentType}/${index}/${filename}`;
    } else if (investmentActivity === ApprovalRequestType.Review) {
      url = `PortfolioCompany/${code}/Review/${quarter}/Attachment/${documentType}/${index}/${filename}`;
    } else {
      url = `PortfolioCompany/${code}/BoardMeeting/${quarter}/${date}/attachment/${documentType}/${index}/${filename}`;
    }
    return this.apiService.delete(url);
  }

  previewFile(attachment: IAttachment): Observable<Blob> {
    return this.apiService.post('CentralRepository/preview', attachment, {
      responseType: 'blob' as 'json'
    });
  }

  uploadFile(ev: QEPFileUploadEvent, indexValue: number): Observable<IAttachment | IAttachment[]> {
    const { rawData, description, date, files } = ev;
    const {
      mnemonic, activityName, investmentActivity,
      documentType, icMemoCategory, icMemoDeal
    } = rawData;

    const formData = new FormData();
    (files as File[]).forEach(file => {
      formData.append('files', file, file.name);
    });

    let url: string;
    let uploadDescription = description;
    const index = indexValue;
    let params: any = {};

    if ((investmentActivity as any) === ApprovalRequestType.Valuation) {
      // ── Valuation ───────────────────────────────────────────────────────────
      if (documentType === AttachmentType.valuationWriteUp) {
        uploadDescription = uploadDescription || rawData.writeUpFund;
        if (!uploadDescription.includes('(')) uploadDescription = '(' + uploadDescription;
        if (!uploadDescription.includes(')')) uploadDescription += ')';
        const start = uploadDescription.indexOf('(');
        const end = uploadDescription.indexOf(')');
        if (end < start) uploadDescription += ')';
      }
      url = `PortfolioCompany/${mnemonic}/Valuation/${activityName}/Attachment/${documentType}/${index}`;
      params = { description: uploadDescription };

    } else if ((investmentActivity as any) === ApprovalRequestType.Review) {
      // ── Review ──────────────────────────────────────────────────────────────
      url = `PortfolioCompany/${mnemonic}/Review/${activityName}/Attachment/${documentType}/${index}`;
      params = { description: uploadDescription };

    } else if ((investmentActivity as any) === InvestmentActivityFileType.IC_Memo) {
      url = `PortfolioCompany/${mnemonic}/ICMemo/${activityName}/${date}/attachment/${documentType}/${index}`;
      params = {
        ...(uploadDescription && { description: uploadDescription }),
        ...(icMemoCategory && { icMemoCategory }),
        ...(icMemoDeal?.trim() && { icMemoDeal: icMemoDeal.trim() }),
        ...(date && { date })
      };

    } else if ((investmentActivity as any) === InvestmentActivityFileType.Legal) {
      // Legal
      url = `PortfolioCompany/${mnemonic}/legal/${activityName}/${date}/attachment/${documentType}/${index}`;
      params = { description: uploadDescription };
    } else if ((investmentActivity as any) === InvestmentActivityFileType.Technical_Materials) {
      // Technical Materials
      url = `PortfolioCompany/${mnemonic}/technical_materials/${activityName}/${date}/attachment/${documentType}/${index}`;
      params = { description: uploadDescription };
    } else {
      // ── Board Materials ─────────────────────────────────────────────────────
      url = `PortfolioCompany/${mnemonic}/BoardMeeting/${activityName}/${date}/attachment/${documentType}/${index}`;
      params = { description: uploadDescription };
    }

    return this.apiService.post(url, formData, { params });
  }

  getVersionAttachmentList(
    mnemonic: string,
    investmentActivity: string,
    fileName: string,
    quarter: string,
    date: string
  ): Observable<IStorageStat[]> {

    let url: string;

    if (investmentActivity === ApprovalRequestType.Valuation) {
      url = `PortfolioCompany/${mnemonic}/Valuation/${quarter}/Attachment/${fileName}`;

    } else if (investmentActivity === ApprovalRequestType.Review) {
      url = `PortfolioCompany/${mnemonic}/Review/${quarter}/Attachment/${fileName}`;

    } else if (investmentActivity === InvestmentActivityFileType.IC_Memo) {
      url = `PortfolioCompany/${mnemonic}/ICMemo/${quarter}/${date}/Attachment/${fileName}`;
    } else if (investmentActivity === InvestmentActivityFileType.Legal) {
      url = `PortfolioCompany/${mnemonic}/legal/${quarter}/${date}/Attachment/${fileName}`;
    } else if (investmentActivity === InvestmentActivityFileType.Technical_Materials) {
      url = `PortfolioCompany/${mnemonic}/technical_materials/${quarter}/${date}/Attachment/${fileName}`;
    } else {
      url = `PortfolioCompany/${mnemonic}/BoardMeeting/${quarter}/${date}/Attachment/${fileName}`;
    }

    return this.apiService.get<IStorageStat[]>(url);
  }

  downloadAttachmentVersion(
    mnemonic: string,
    investmentActivity: string,
    quarter: string,
    attachment: any,
    version: string,
    date: string
  ): Observable<Blob> {

    let url: string;

    if (investmentActivity === ApprovalRequestType.Valuation) {
      url = `PortfolioCompany/${mnemonic}/Valuation/${quarter}/Attachment/${version}`;

    } else if (investmentActivity === ApprovalRequestType.Review) {
      url = `PortfolioCompany/${mnemonic}/Review/${quarter}/Attachment/${version}`;

    } else if (investmentActivity === InvestmentActivityFileType.IC_Memo) {
      url = `PortfolioCompany/${mnemonic}/ICMemo/${quarter}/${date}/Attachment/${version}`;
    } else if (investmentActivity === InvestmentActivityFileType.Legal) {
      url = `PortfolioCompany/${mnemonic}/legal/${quarter}/${date}/attachment/${version}`;
    } else if (investmentActivity === InvestmentActivityFileType.Technical_Materials) {
      url = `PortfolioCompany/${mnemonic}/technical_materials/${quarter}/${date}/attachment/${version}`;
    } else {
      url = `PortfolioCompany/${mnemonic}/BoardMeeting/${quarter}/${date}/Attachment/${version}`;
    }

    return this.apiService.post(url, attachment, {
      headers: new HttpHeaders().set('Accept', attachment.mimeType),
      responseType: 'blob'
    });
  }
}